import { useCallback, useEffect, useState } from "react";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Runs an async loader on mount (and whenever `deps` change), tracking
 * loading / error / data. Guarantees a page always has an explicit state to
 * render — no uncaught rejections, no blank screens on a failed fetch.
 *
 *   const { data, loading, error, reload } = useAsync(() => apiFetch("/x"), []);
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    loader()
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, reload };
}

/** Debounced assistance never shows a previous request/client/language result.
 * The key must include every input used by the loader. Aborts old network work. */
export function useDebouncedAsync<T>(key: string | null, loader: (signal: AbortSignal) => Promise<T>, delay = 600): AsyncState<T> {
  const [state, setState] = useState<{key: string; data: T | null; loading: boolean; error: string | null} | null>(null);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce(value => value + 1), []);
  useEffect(() => {
    if (key === null) return;
    let active = true;
    const controller = new AbortController();
    setState({key, data: null, loading: true, error: null});
    const timer = setTimeout(() => {
      void loader(controller.signal).then(data => {
        if (active) setState({key, data, loading: false, error: null});
      }).catch((error: unknown) => {
        if (active) setState({key, data: null, loading: false, error: error instanceof Error ? error.message : String(error)});
      });
    }, delay);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
    // The serialized key declares the loader's complete input, including locale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, delay, nonce]);
  return {data: key !== null && state?.key === key ? state.data : null,
    loading: key !== null && (state?.key !== key || state.loading),
    error: key !== null && state?.key === key ? state.error : null, reload};
}
